// The sonar sweep's own program (card 59, R548, ui-map-interface.md
// §17.3.59).
//
// It is the ONE per-pixel term of the chart, and it is confined: one quad of
// `2R × 2R` over a running reveal ring, about 0.13 Mpx at 1×. The fragment
// is one `atan`, one `fract` and a four-stop ramp — about twelve ALU and no
// texture fetch at all.
//
// The afterglow is the mockup's conic gradient, written as arithmetic. The
// canvas gradient starts at the radius and runs one whole turn clockwise, so
// the water JUST BEHIND the radius is the brightest and the water just ahead
// of it is empty. `fract((theta - angle) / TAU)` is that fraction, and the
// four stops of `[chart.piece.sonar_sweep.arr]` are the ramp over it.
//
// The sources are plain strings, so `sweep.test.ts` reads them without a GL
// context and pins the uniform contract the pass writes.

import { CORNER_ATTRIB, CORNER_DECODE_GLSL } from '../gl/corner';
import type { UniformSpec } from './prelude';
import type { ProgramSource } from './program';
import { sweepModuleWgsl } from './wgsl/sweep';

/**
 * Every uniform the two sources declare, TYPED and in order.
 *
 * The order is the contract on WebGPU, where the block is one buffer at byte
 * offsets, so it is stated here and the name list below is read from it.
 * Every one is `dynamic`: the pass writes the whole block on every draw,
 * because one frame draws one quad per running reveal.
 */
export const SWEEP_UNIFORM_SPECS: readonly UniformSpec[] = [
  { name: 'uViewProj', type: 'mat3', value: [1, 0, 0, 0, 1, 0, 0, 0, 1], dynamic: true },
  { name: 'uCentre', type: 'vec2', value: [0, 0], dynamic: true },
  { name: 'uR', type: 'float', value: 0, dynamic: true },
  { name: 'uAngle', type: 'float', value: 0, dynamic: true },
  { name: 'uRgb', type: 'vec3', value: [1, 1, 1], dynamic: true },
  { name: 'uFade', type: 'float', value: 1, dynamic: true },
  { name: 'uStopAt', type: 'vec4', value: [0, 0, 0, 0], dynamic: true },
  { name: 'uStopAlpha', type: 'vec4', value: [0, 0, 0, 0], dynamic: true },
  { name: 'uLine', type: 'vec2', value: [0, 0], dynamic: true },
];

/** The same names, as `sweep-pass.ts` and its test read them. */
export const SWEEP_UNIFORMS = SWEEP_UNIFORM_SPECS.map((u) => u.name);

/**
 * The quad, in WORLD px.
 *
 * `aCorner` is the per-vertex corner index of `gl/corner.ts` (R553), and it
 * makes the four corners of `[centre − R, centre + R]`. The pass holds a
 * four-float buffer and one vertex array for it, and nothing else.
 */
export const SWEEP_VERTEX = `#version 300 es
precision highp float;
layout(location=0) in float aCorner;
uniform mat3 uViewProj;
uniform vec2 uCentre;
uniform float uR;
out vec2 vWorld;
void main() {
${CORNER_DECODE_GLSL}
  vWorld = uCentre + (corner * 2.0 - 1.0) * uR;
  gl_Position = vec4((uViewProj * vec3(vWorld, 1.0)).xy, 0.0, 1.0);
}`;

/**
 * The afterglow and the radius.
 *
 * The output is PREMULTIPLIED, because the one blend state of `gl/context.ts`
 * is `blendFunc(ONE, ONE_MINUS_SRC_ALPHA)`.
 *
 * `uLine` is the radius: its alpha and its half width in world px. The line
 * is a distance to the ray and not a second draw call, so the whole sweep
 * stays one quad.
 */
export const SWEEP_FRAGMENT = `#version 300 es
precision highp float;
in vec2 vWorld;
uniform vec2 uCentre;
uniform float uR;
uniform float uAngle;
uniform vec3 uRgb;
uniform float uFade;
uniform vec4 uStopAt;
uniform vec4 uStopAlpha;
uniform vec2 uLine;
out vec4 o;

const float TAU = 6.28318530718;

void main() {
  vec2 d = vWorld - uCentre;
  float rr = length(d);
  if (rr > uR) discard;
  float t = fract((atan(d.y, d.x) - uAngle) / TAU);
  // The four-stop ramp, as three clamped mixes.
  float a = mix(uStopAlpha.x, uStopAlpha.y,
    clamp((t - uStopAt.x) / max(1e-5, uStopAt.y - uStopAt.x), 0.0, 1.0));
  a = mix(a, uStopAlpha.z,
    clamp((t - uStopAt.y) / max(1e-5, uStopAt.z - uStopAt.y), 0.0, 1.0));
  a = mix(a, uStopAlpha.w,
    clamp((t - uStopAt.z) / max(1e-5, uStopAt.w - uStopAt.z), 0.0, 1.0));
  // The turning radius: the distance from the point to the ray.
  vec2 ray = vec2(cos(uAngle), sin(uAngle));
  float along = dot(d, ray);
  float off = abs(d.x * ray.y - d.y * ray.x);
  if (along > 0.0) {
    a = max(a, uLine.x * (1.0 - smoothstep(uLine.y * 0.5, uLine.y, off)));
  }
  a *= uFade;
  o = vec4(uRgb * a, a);
}`;

/**
 * The sweep as ONE program, in both languages.
 *
 * It carries no shared world block: the pass draws over the batches of a
 * chart tier, it reads no data plane and it holds its own nine uniforms. The
 * corner is its only attribute, so the WGSL vertex input is generated from
 * that one entry (R553).
 */
export function sweepProgram(): ProgramSource {
  const attribs = [CORNER_ATTRIB];
  return {
    name: 'sweep',
    glsl: { vertex: SWEEP_VERTEX, fragment: SWEEP_FRAGMENT },
    wgsl: sweepModuleWgsl(SWEEP_UNIFORM_SPECS, attribs),
    uniforms: SWEEP_UNIFORM_SPECS,
    attribs,
  };
}
